import React from 'react';
import { AccordionItem } from '../components/GenericAccordion';
import { TreatmentRound } from '../components/TreatmentRound';
import { TreatmentSummaryCard } from '../components/TreatmentSummaryCard';
import RecommendationStep from '../components/RecommendationStep';
import { MildSectionKey, AssessmentData } from '../utils/asthmaTypes';
import { InitialAssessmentSummary } from '../components/InitialAssessmentSummary';
import { MEDICATION_GROUPS } from '../utils/asthmaMedicationsData';
import { useAsthmaAssessment } from './useAsthmaAssessment';
import { useAsthmaFlow } from './useAsthmaFlow';

interface UseAsthmaAccordionItemsProps {
  assessment: ReturnType<typeof useAsthmaAssessment>;
  flow: ReturnType<typeof useAsthmaFlow>;
  improvedFromSevere: boolean;
  setImprovedFromSevere: (val: boolean) => void;
  isEmailSent: boolean;
  isLoggedIn: boolean;
  handleToggleSection: (section: MildSectionKey) => void;
  handleRecheckDone: (round: number, data: AssessmentData) => void;
  handleReset: () => void;
  handleStartRecheck: (round: number) => void;
  setShowCloseConfirmation: (val: boolean) => void;
  setShowPrematureClosePopup: (val: boolean) => void;
  onSendEmailClick: () => void;
  initialData?: AssessmentData | null;
}

export const useAsthmaAccordionItems = ({
  assessment,
  flow,
  improvedFromSevere,
  setImprovedFromSevere,
  isEmailSent,
  isLoggedIn,
  handleToggleSection,
  handleRecheckDone,
  handleReset,
  handleStartRecheck,
  setShowCloseConfirmation,
  setShowPrematureClosePopup,
  onSendEmailClick,
  initialData,
}: UseAsthmaAccordionItemsProps): AccordionItem[] => {

  const steroidLabels = MEDICATION_GROUPS.find(g => g.id === 'steroids')?.options.map(o => o.label) || [];
  const hasGivenSteroids = flow.medicationLog.some(entry =>
    entry.medications.some(medLabel => steroidLabels.includes(medLabel))
  );

  const items: AccordionItem[] = [];

  // Selecting data source: If there's a Snapshot, use it. Otherwise, use live data.
  // We perform Normalization so the names match the component
  const initialSource = initialData
    ? {
      age: initialData.age,
      respiratoryRate: initialData.respiratoryRate,
      oxygenSaturation: initialData.oxygenSaturation,
      oxygenSupport: initialData.oxygenSupport,
      auscultation: initialData.auscultation,
      retractions: initialData.retractions,
      dyspnea: initialData.dyspnea,
      score: initialData.score,
      severityLabel: initialData.severityLabel,
      activeSeverity: initialData.activeSeverity,
      initialAssessmentTime: initialData.timestamp
    }
    : {
      age: assessment.age,
      respiratoryRate: assessment.respiratoryRate,
      oxygenSaturation: assessment.oxygenSaturation,
      oxygenSupport: assessment.oxygenSupport,
      auscultation: assessment.auscultation,
      retractions: assessment.retractions,
      dyspnea: assessment.dyspnea,
      score: assessment.score,
      severityLabel: assessment.severity,
      activeSeverity: assessment.activeSeverity,
      initialAssessmentTime: assessment.initialAssessmentTime
    };

  // === 1. initial assessment ===
  items.push({
    key: 'INITIAL',
    title: 'הערכה ראשונית',
    isOpen: flow.openSection === 'INITIAL',
    onToggle: () => flow.setOpenSection(flow.openSection === 'INITIAL' ? null : 'INITIAL'),
    content: (
      <InitialAssessmentSummary
        age={initialSource.age}
        respiratoryRate={initialSource.respiratoryRate}
        oxygenSaturation={initialSource.oxygenSaturation}
        oxygenSupport={initialSource.oxygenSupport}
        auscultation={initialSource.auscultation}
        retractions={initialSource.retractions}
        dyspnea={initialSource.dyspnea}
        score={initialSource.score}
        severityLabel={initialSource.severityLabel}
        activeSeverity={initialSource.activeSeverity}
        initialAssessmentTime={initialSource.initialAssessmentTime}
      />
    ),
  });

  if (!assessment.activeSeverity) return items;

  // === 2. first round treatment ===
  items.push({
    key: 'TREATMENT',
    title: 'טיפול תרופתי',
    isOpen: flow.openSection === 'TREATMENT',
    onToggle: () => handleToggleSection('TREATMENT'),
    content: (
      <TreatmentRound
        round={1}
        activeSeverity={assessment.activeSeverity}
        medicationLog={flow.medicationLog}
        dosesAtStartOfRound2={flow.dosesAtStartOfRound2}
        secondRoundStarted={flow.secondRoundStarted}
        thirdRoundStarted={flow.thirdRoundStarted}
        recheckDone={flow.recheckDone}
        hasGivenSteroids={hasGivenSteroids}
        setCurrentMedications={flow.setCurrentMedications}
        handleDoseGiven={flow.handleDoseGiven}
        onStartRecheck={() => handleStartRecheck(1)}
      />
    ),
  });

  // === 3. first recheck ===
  items.push({
    key: 'RECHECK',
    title: 'הערכה חוזרת – שנייה',
    isOpen: flow.openSection === 'RECHECK',
    onToggle: () => handleToggleSection('RECHECK'),
    content: flow.recheckResults[1]
      ? (
        <InitialAssessmentSummary
          age={flow.recheckResults[1].age}
          respiratoryRate={flow.recheckResults[1].respiratoryRate}
          oxygenSaturation={flow.recheckResults[1].oxygenSaturation}
          oxygenSupport={flow.recheckResults[1].oxygenSupport}
          auscultation={flow.recheckResults[1].auscultation}
          retractions={flow.recheckResults[1].retractions}
          dyspnea={flow.recheckResults[1].dyspnea}
          score={flow.recheckResults[1].score}
          severityLabel={flow.recheckResults[1].severityLabel}
          activeSeverity={flow.recheckResults[1].activeSeverity}
          initialAssessmentTime={flow.recheckResults[1].timestamp}
        />
      )
      : null,
  });

  // === 4. first recommendations ===
  items.push({
    key: 'RECOMMENDATIONS',
    title: 'המלצות להמשך טיפול',
    isOpen: flow.openSection === 'RECOMMENDATIONS',
    onToggle: () => flow.setOpenSection(flow.openSection === 'RECOMMENDATIONS' ? null : 'RECOMMENDATIONS'),
    content: (
      <RecommendationStep
        step={1}
        activeSeverity={assessment.activeSeverity}
        secondRoundStarted={flow.secondRoundStarted}
        thirdRoundStarted={flow.thirdRoundStarted}
        improvedFromSevere={improvedFromSevere}
        totalDosesGiven={flow.medicationLog.length}
        onStartSecondRound={() => {
          flow.setDosesAtStartOfRound2(flow.medicationLog.length);
          flow.setSecondRoundStarted(true);
          flow.setRecheckDone(false);
          setImprovedFromSevere(false);
          flow.setOpenSection('TREATMENT_2');
        }}
        onStartThirdRound={() => { }}
        onExit={() => setShowPrematureClosePopup(true)}
      />
    ),
  });

  // === second round (if relevant) ===
  if (flow.secondRoundStarted) {
    items.push({
      key: 'TREATMENT_2',
      title: 'טיפול תרופתי – שני',
      isOpen: flow.openSection === 'TREATMENT_2',
      onToggle: () => handleToggleSection('TREATMENT_2'),
      content: (
        <TreatmentRound
          round={2}
          activeSeverity={assessment.activeSeverity}
          medicationLog={flow.medicationLog}
          dosesAtStartOfRound2={flow.dosesAtStartOfRound2}
          secondRoundStarted={flow.secondRoundStarted}
          thirdRoundStarted={flow.thirdRoundStarted}
          recheckDone={flow.recheckDone}
          hasGivenSteroids={hasGivenSteroids}
          setCurrentMedications={flow.setCurrentMedications}
          handleDoseGiven={flow.handleDoseGiven}
          onStartRecheck={() => handleStartRecheck(2)}
        />
      ),
    });

    items.push({
      key: 'RECHECK_2',
      title: 'הערכה חוזרת – שלישית',
      isOpen: flow.openSection === 'RECHECK_2',
      onToggle: () => handleToggleSection('RECHECK_2'),
      content: flow.recheckResults[2]
        ? (
          <InitialAssessmentSummary
            age={flow.recheckResults[2].age}
            respiratoryRate={flow.recheckResults[2].respiratoryRate}
            oxygenSaturation={flow.recheckResults[2].oxygenSaturation}
            oxygenSupport={flow.recheckResults[2].oxygenSupport}
            auscultation={flow.recheckResults[2].auscultation}
            retractions={flow.recheckResults[2].retractions}
            dyspnea={flow.recheckResults[2].dyspnea}
            score={flow.recheckResults[2].score}
            severityLabel={flow.recheckResults[2].severityLabel}
            activeSeverity={flow.recheckResults[2].activeSeverity}
            initialAssessmentTime={flow.recheckResults[2].timestamp}
          />
        )
        : null,
    });

    items.push({
      key: 'RECOMMENDATIONS_2',
      title: 'המלצות להמשך טיפול – שנייה',
      isOpen: flow.openSection === 'RECOMMENDATIONS_2',
      onToggle: () => flow.setOpenSection(flow.openSection === 'RECOMMENDATIONS_2' ? null : 'RECOMMENDATIONS_2'),
      content: (
        <RecommendationStep
          step={2}
          activeSeverity={assessment.activeSeverity}
          secondRoundStarted={flow.secondRoundStarted}
          thirdRoundStarted={flow.thirdRoundStarted}
          improvedFromSevere={improvedFromSevere}
          totalDosesGiven={flow.medicationLog.length}
          dosesAtStartOfRound2={flow.dosesAtStartOfRound2}
          onStartSecondRound={() => { }}
          onStartThirdRound={() => {
            flow.setDosesAtStartOfRound3(flow.medicationLog.length);
            flow.setThirdRoundStarted(true);
            flow.setRecheckDone(false);
            flow.setOpenSection('TREATMENT_3');
          }}
          onExit={() => setShowPrematureClosePopup(true)}
        />
      ),
    });
  }

  // === third round (if relevant) ===
  if (flow.thirdRoundStarted) {
    items.push({
      key: 'TREATMENT_3',
      title: 'טיפול תרופתי – שלישי',
      isOpen: flow.openSection === 'TREATMENT_3',
      onToggle: () => handleToggleSection('TREATMENT_3'),
      content: (
        <TreatmentRound
          round={3}
          activeSeverity={assessment.activeSeverity}
          medicationLog={flow.medicationLog}
          dosesAtStartOfRound2={flow.dosesAtStartOfRound2}
          secondRoundStarted={flow.secondRoundStarted}
          thirdRoundStarted={flow.thirdRoundStarted}
          recheckDone={flow.recheckDone}
          hasGivenSteroids={hasGivenSteroids}
          setCurrentMedications={flow.setCurrentMedications}
          handleDoseGiven={flow.handleDoseGiven}
          onStartRecheck={() => handleStartRecheck(3)}
        />
      ),
    });

    items.push({
      key: 'RECHECK_3',
      title: 'הערכה חוזרת – רביעית',
      isOpen: flow.openSection === 'RECHECK_3',
      onToggle: () => handleToggleSection('RECHECK_3'),
      content: flow.recheckResults[3]
        ? (
          <InitialAssessmentSummary
            age={flow.recheckResults[3].age}
            respiratoryRate={flow.recheckResults[3].respiratoryRate}
            oxygenSaturation={flow.recheckResults[3].oxygenSaturation}
            oxygenSupport={flow.recheckResults[3].oxygenSupport}
            auscultation={flow.recheckResults[3].auscultation}
            retractions={flow.recheckResults[3].retractions}
            dyspnea={flow.recheckResults[3].dyspnea}
            score={flow.recheckResults[3].score}
            severityLabel={flow.recheckResults[3].severityLabel}
            activeSeverity={flow.recheckResults[3].activeSeverity}
            initialAssessmentTime={flow.recheckResults[3].timestamp}
          />
        )
        : null,
    });

    items.push({
      key: 'RECOMMENDATIONS_3',
      title: 'המלצות להמשך טיפול – שלישית',
      isOpen: flow.openSection === 'RECOMMENDATIONS_3',
      onToggle: () => flow.setOpenSection(flow.openSection === 'RECOMMENDATIONS_3' ? null : 'RECOMMENDATIONS_3'),
      content: (
        <RecommendationStep
          step={3}
          activeSeverity={assessment.activeSeverity}
          secondRoundStarted={flow.secondRoundStarted}
          thirdRoundStarted={flow.thirdRoundStarted}
          improvedFromSevere={improvedFromSevere}
          totalDosesGiven={flow.medicationLog.length}
          dosesAtStartOfRound3={flow.dosesAtStartOfRound3}
          onStartSecondRound={() => { }}
          onStartThirdRound={() => { }}
          onExit={() => setShowPrematureClosePopup(true)}
        />
      ),
    });
  }

  // === summary ===
  items.push({
    key: 'SUMMARY',
    title: 'סיכום וסגירת טיפול',
    isOpen: flow.openSection === 'SUMMARY',
    onToggle: () => flow.setOpenSection(flow.openSection === 'SUMMARY' ? null : 'SUMMARY'),
    content: (
      <TreatmentSummaryCard
        isEmailSent={isEmailSent}
        isLoggedIn={isLoggedIn}
        onSendEmailClick={onSendEmailClick}
        onCloseTreatmentClick={() => {
          if (isEmailSent) handleReset();
          else setShowCloseConfirmation(true);
        }}
        onBackToRecommendations={flow.setOpenSection}
        secondRoundStarted={flow.secondRoundStarted}
        thirdRoundStarted={flow.thirdRoundStarted}
      />
    ),
  });

  return items;
};